import { useCallback } from "react";
import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, InputNumber, SubmitButton } from "formik-antd";
import { useTranslation } from "react-i18next";
import { Setting as SettingType } from "/app/src/models";
import { settingService } from "/app/src/services";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { buildParams } from "/app/src/helpers";
import { handlePromiseError } from "/app/src/helpers/api";
/**
 * Component to display the form for updating API call log retention days
 */
export default function ApiCallLogRetentionDays() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  //useQuery for getting the API call log retention days
  const { data: apiCallLogRetentionDays } = useQuery({
    queryKey: ["apiCallLogRetentionDays"],
    queryFn: () => {
      return settingService
        .getAll(buildParams({ name: "apiCallLogRetentionDays" }))
        .then((response) => {
          return response.settings[0];
        });
    },
    initialData: { value: undefined },
  });
  //useMutation for updating the API call log retention days
  const { mutateAsync: updateRetentionDays } = useMutation({
    mutationFn: (updateRetentionDays: SettingType) => {
      return settingService
        .updateSingle(updateRetentionDays.id, {
          value: updateRetentionDays.value,
        })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["apiCallLogRetentionDays"], data.setting);
    },
  });
  //useMutation for creating the API call log retention days
  const { mutateAsync: createRetentionDays } = useMutation({
    mutationFn: (createRetentionDays: SettingType) => {
      return settingService
        .createSingle({
          value: createRetentionDays.value,
          name: "apiCallLogRetentionDays",
          number: 111,
        })
        .then(handlePromiseError);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["apiCallLogRetentionDays"], data.setting);
    },
  });
  //onSubmit handler for creating/updating the API call log retention days
  const onSubmitRetentionDays = useCallback(
    async (
      {
        value,
      }: {
        value: number;
      },
      {
        setFieldError,
      }: { setFieldError: (field: string, value: string) => void },
    ): Promise<void> => {
      const valueStr = String(value);
      if (apiCallLogRetentionDays?.id) {
        await updateRetentionDays({
          id: apiCallLogRetentionDays.id,
          value: valueStr,
        }).catch((error) => {
          return setFieldError("value", error.errors[0]);
        });
      } else {
        await createRetentionDays({ value: valueStr }).catch((error) => {
          return setFieldError("value", error.errors[0]);
        });
      }
    },
    [createRetentionDays, updateRetentionDays, apiCallLogRetentionDays?.id],
  );
  /**
   * Update retention days form
   */
  const updateRetentionDaysForm: (
    props: FormikProps<{ value: number }>,
  ) => JSX.Element = useCallback(
    ({ dirty }) => (
      <Form layout="vertical">
        <Row justify="start" gutter={16}>
          <Col span={8}>
            <h3>{`${t("translation:api_call_log_retention_days")}`}</h3>
            <p>{t("translation:api_call_log_retention_days_description")}</p>
          </Col>
          <Col span={13}>
            <Form.Item
              className="no-label" // skipcq: JS-0394
              name="value"
            >
              <InputNumber name="value" size="large" min={1} max={3650} />
            </Form.Item>
          </Col>
          <Col span={3}>
            <SubmitButton
              className="no-label" // skipcq: JS-0394
              type="primary"
              size="large"
              block
              disabled={!dirty}
            >
              {t("translation:save")}
            </SubmitButton>
          </Col>
        </Row>
      </Form>
    ),
    [t],
  );
  return (
    <Formik
      component={updateRetentionDaysForm}
      enableReinitialize
      initialValues={{
        value: apiCallLogRetentionDays?.value
          ? parseInt(apiCallLogRetentionDays.value, 10)
          : 30,
      }}
      onSubmit={onSubmitRetentionDays}
    />
  );
}
